import { useState } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { 
  Bars3BottomLeftIcon, 
  ChevronUpIcon, 
  ChevronDownIcon, 
  EyeIcon, 
  EyeSlashIcon,
  ArrowsUpDownIcon
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

// Initial navigation matches components/Layout.tsx
const INITIAL_NAVIGATION = [
  { id: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: 'HomeIcon', visible: true },
  { id: 'tasks', name: 'Tasks', href: '/tasks', icon: 'ClipboardDocumentListIcon', visible: true },
  { id: 'crm', name: 'CRM', href: '/crm', icon: 'UserGroupIcon', visible: true },
  { id: 'notes', name: 'Notes', href: '/notes', icon: 'DocumentTextIcon', visible: true },
  { id: 'insights', name: 'Insights', href: '/insights', icon: 'ChartBarIcon', visible: true },
  { id: 'assistant', name: 'Assistant', href: '/assistant', icon: 'SparklesIcon', visible: true },
  { id: 'build-review', name: 'Build Review', href: '/build-review', icon: 'ShieldCheckIcon', visible: true },
  { id: 'team', name: 'Team', href: '/team', icon: 'UsersIcon', visible: true },
];

interface NavigationItem {
  id: string;
  name: string;
  href: string;
  icon: string;
  visible: boolean;
}

export function SidebarCustomizer() {
  const { currentWorkspace } = useWorkspace();
  const [items, setItems] = useState<NavigationItem[]>(() => {
    const saved = localStorage.getItem(`sidebar_config_${currentWorkspace?.id || 'default'}`);
    return saved ? JSON.parse(saved) : INITIAL_NAVIGATION;
  });

  const saveConfig = (newItems: NavigationItem[]) => {
    setItems(newItems);
    localStorage.setItem(`sidebar_config_${currentWorkspace?.id || 'default'}`, JSON.stringify(newItems));
    // Trigger storage event for Layout.tsx to pick up
    window.dispatchEvent(new Event('sidebar_config_updated'));
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const newItems = [...items];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    saveConfig(newItems);
  };

  const moveDown = (index: number) => {
    if (index === items.length - 1) return;
    const newItems = [...items];
    [newItems[index + 1], newItems[index]] = [newItems[index], newItems[index + 1]];
    saveConfig(newItems);
  };

  const toggleVisibility = (index: number) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], visible: !newItems[index].visible };
    saveConfig(newItems);
  };

  const resetToDefault = () => {
    saveConfig(INITIAL_NAVIGATION);
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sidebar Customizer</h1>
          <p className="text-sm text-gray-500 mt-1">
            Organize and manage your workspace navigation. (LIG-1319)
          </p>
        </div>
        <button 
          onClick={resetToDefault}
          className="text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          Reset to default
        </button>
      </div>

      <div className="card shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bars3BottomLeftIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Navigation Items</span>
          </div>
          <span className="badge badge-info">Manual Sorting Mode</span>
        </div>
        
        <div className="divide-y divide-gray-100">
          {items.map((item: NavigationItem, index: number) => (
            <div 
              key={item.id} 
              className={clsx(
                "flex items-center justify-between p-4 transition-colors",
                !item.visible ? "bg-gray-50/50" : "bg-white hover:bg-gray-50/30"
              )}
            >
              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-0.5">
                  <button 
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    className="p-0.5 text-gray-400 hover:text-primary-600 disabled:opacity-20 transition-colors"
                  >
                    <ChevronUpIcon className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={() => moveDown(index)}
                    disabled={index === items.length - 1}
                    className="p-0.5 text-gray-400 hover:text-primary-600 disabled:opacity-20 transition-colors"
                  >
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                </div>
                
                <div className={clsx(
                  "flex items-center gap-3",
                  !item.visible && "opacity-50 grayscale"
                )}>
                  <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
                    <span className="text-lg">#{index + 1}</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{item.name}</p>
                    <p className="text-xs text-gray-500 font-mono">{item.href}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => toggleVisibility(index)}
                  className={clsx(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all border",
                    item.visible 
                      ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100" 
                      : "bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200"
                  )}
                >
                  {item.visible ? (
                    <>
                      <EyeIcon className="h-3.5 w-3.5" />
                      Visible
                    </>
                  ) : (
                    <>
                      <EyeSlashIcon className="h-3.5 w-3.5" />
                      Hidden
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 p-4 rounded-xl bg-primary-50 border border-primary-100 flex items-start gap-4">
        <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
          <ArrowsUpDownIcon className="h-5 w-5 text-primary-600" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-primary-900">Sorting & Visibility Tip</h3>
          <p className="text-xs text-primary-700 mt-1 leading-relaxed">
            Move items up to prioritize them in your sidebar. Hidden items will remain accessible via direct URL but won't clutter your workspace navigation. Changes are saved instantly to your workspace.
          </p>
        </div>
      </div>
    </div>
  );
}

export default SidebarCustomizer;
